import { useRef } from 'react';
import { ErrorApi } from './api.ts';

/**
 * Envío único de una operación que no se puede repetir sin daño (una entrega, una guía de traslado, un ajuste, un
 * gasto). Mientras lo enviado sea lo mismo y el servidor no haya respondido (corte de red o error 5xx), el reintento
 * lleva la misma clave y el servidor devuelve lo que ya hizo en vez de repetirlo. Con respuesta, sea éxito o rechazo
 * (4xx: no se hizo nada), la siguiente operación estrena clave. La clave de un envío sin respuesta vale dos minutos:
 * pasado ese tiempo, una operación idéntica es otra operación (otra entrega igual de la misma venta, por ejemplo).
 */
const VIGENCIA_MS = 120_000;

export function useEnvioUnico<D, R>(enviar: (datos: D, clave: string) => Promise<R>): (datos: D) => Promise<R> {
  const pendiente = useRef<{ huella: string; clave: string; desde: number } | null>(null);
  return async (datos: D) => {
    const huella = JSON.stringify(datos);
    const ahora = Date.now();
    if (pendiente.current?.huella !== huella || ahora - pendiente.current.desde > VIGENCIA_MS) pendiente.current = { huella, clave: crypto.randomUUID(), desde: ahora };
    const { clave } = pendiente.current;
    try {
      const r = await enviar(datos, clave);
      pendiente.current = null;
      return r;
    } catch (e) {
      if (e instanceof ErrorApi && e.status < 500) pendiente.current = null;
      throw e;
    }
  };
}

/** Cabecera con la clave de la operación. */
export const conClave = (clave: string) => ({ headers: { 'idempotency-key': clave } });
